import { useParams } from 'react-router-dom';
import {
  useSupplierSpecialPricingRulesBrandQuery,
  useSupplierSpecialPricingRulesCreateMutation,
  useSupplierSpecialPricingRulesUpdateMutation,
} from 'api';
import { getUpdatedPayload } from 'utils/helpers';
import { Form, FormInstance, FormProps } from 'components';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNotificationMessages } from 'hooks/useNotificationMessages';
import { ISpecialPricingRulesAddUpdateModal } from './index';

export interface SupplierSpecialPricingRuleFieldType {
  id: string;
  brandId: string;
  supplierId: string;
  markup: number;
  minLeadTime: number;
  maxLeadTime: number;
  type: string;
  discount: number;
}

export const useSpecialPricingRulesAddEdit = (props: ISpecialPricingRulesAddUpdateModal) => {
  const { dataToEdit, onModalStateChange, modalState } = props;
  const [type, setType] = useState<string>('WEEK');
  const [disableSaveBtn, setDisableSaveBtn] = useState<boolean>(true);
  const { id } = useParams() as { id: string };
  const [form] = Form.useForm();
  const { handleError, handleSuccess } = useNotificationMessages();
  const { data: pricingRuleBrandData = [] } = useSupplierSpecialPricingRulesBrandQuery({ supplierId: id });
  const [createSpecialPricingRule, { isLoading: createIsLoading }] = useSupplierSpecialPricingRulesCreateMutation();
  const [updateSpecialPricingRule, { isLoading: updateIsLoading }] = useSupplierSpecialPricingRulesUpdateMutation();

  const formattedPricingRuleBrandData = useMemo(() => {
    const data = structuredClone(pricingRuleBrandData);
    if (dataToEdit) {
      data.push({
        label: dataToEdit.brand.name,
        value: dataToEdit.brand.id,
      });
    }
    return data;
  }, [dataToEdit, pricingRuleBrandData]);

  const handleTypeChange = useCallback((type: string) => {
    setDisableSaveBtn(false);
    setType(type);
  }, []);

  const onCancel = useCallback(() => {
    form.resetFields();
    onModalStateChange();
  }, [form, onModalStateChange]);

  const clearFieldValidation = useCallback((form: FormInstance, field: string) => {
    const hasError = form.getFieldError(field)?.length;
    if (hasError) {
      form.setFields([
        {
          name: field,
          errors: [], // Clears the error for filed
        },
      ]);
    }
  }, []);

  const onFinish: FormProps<SupplierSpecialPricingRuleFieldType>['onFinish'] = useCallback(
    async (values: SupplierSpecialPricingRuleFieldType) => {
      const payload = getUpdatedPayload(values, dataToEdit);
      if (dataToEdit) {
        payload.id = dataToEdit.id;
      }
      payload.supplierId = id;
      payload.type = type;
      try {
        const { data, error }: Record<string, any> = await (dataToEdit
          ? updateSpecialPricingRule(payload)
          : createSpecialPricingRule(payload));
        if (data?.success) {
          handleSuccess(`Special Pricing Rule was successfully ${dataToEdit ? 'updated' : 'created'}`);
          onCancel();
        } else {
          handleError(error);
        }
      } catch (err) {
        handleError(err);
      }
    },
    [createSpecialPricingRule, handleSuccess, handleError, updateSpecialPricingRule, dataToEdit, onCancel, id, type]
  );

  useEffect(() => {
    if (dataToEdit && modalState) {
      form.setFieldsValue({ ...dataToEdit, brandId: dataToEdit.brand.id });
      setType(dataToEdit.type);
    }
  }, [dataToEdit, form, modalState]);
  return {
    onFinish,
    onCancel,
    form,
    disableSaveBtn,
    handleTypeChange,
    type,
    clearFieldValidation,
    formattedPricingRuleBrandData,
    isLoading: updateIsLoading || createIsLoading,
  };
};
